import React from 'react';
import ResponsiveECharts from './ResponsiveECharts';
import { baseOption, axeValeur } from './echartsTheme';

/**
 * Classement des solutions d'adaptation, en nuage de points.
 *
 *   axe X : impact de la solution — écart entre le risque de l'horizon choisi
 *           et celui de la référence (2020, ou Référence en TRACC).
 *           À gauche impact faible, à droite impact fort.
 *   axe Y : robustesse — écart-type du résultat entre scénarios.
 *           En bas "dépend des scénarios", en haut "sans regret".
 *
 * Les deux valeurs sont calculées côté API : ce composant ne fait que placer
 * les points et colorer les quadrants.
 *
 *   haut-gauche  jaune   impact faible mais sans regret
 *   haut-droite  vert    impact fort et sans regret : les meilleures solutions
 *   bas-gauche   rouge   impact faible et dépendant des scénarios
 *   bas-droite   orange  impact fort mais dépendant des scénarios
 */
const QUADRANTS = [
  { x: [0, 0.5], y: [0.5, 1], couleur: 'rgba(255, 235, 132, 0.35)' },
  { x: [0.5, 1], y: [0.5, 1], couleur: 'rgba(146, 208, 80, 0.35)' },
  { x: [0, 0.5], y: [0, 0.5], couleur: 'rgba(230, 124, 115, 0.35)' },
  { x: [0.5, 1], y: [0, 0.5], couleur: 'rgba(244, 177, 92, 0.35)' },
];

export default function ClassementAdaptationsChart({ data = [] }) {
  if (data.length === 0) {
    return <p className="tdb-chargement">Aucune solution à afficher.</p>;
  }

  const impacts = data.map((d) => d.impact);
  const robustesses = data.map((d) => d.ecartType);

  // Les quadrants se découpent autour de 0 en X (gain ou perte) et autour de la
  // médiane des écarts-types en Y : le seuil "sans regret" est relatif aux
  // solutions comparées, pas une valeur absolue.
  // Axe X symétrique autour de 0 : borne = max(|impact|), de -borne à +borne,
  // pour que la lecture gain/perte soit uniforme de part et d'autre.
  const borne = Math.max(...impacts.map((v) => Math.abs(v))) || 1;
  const xMin = -borne;
  const xMax = borne;
  const yMin = 0;
  const yMax = Math.max(...robustesses) * 1.15 || 1;

  // L'axe Y se lit à l'envers : un écart-type faible = solution sans regret.
  const versY = (ecartType) => yMax - ecartType;

  const option = {
    ...baseOption(),
    legend: { show: false },
    grid: { left: 90, right: 40, bottom: 70, top: 56, containLabel: true },
    tooltip: {
      trigger: 'item',
      textStyle: { fontSize: 14, color: '#000000' },
      formatter: ({ data: point }) =>
        `<b>${point.numero}. ${point.nom}</b><br/>Impact : ${point.value[0].toFixed(1)} pts` +
        `<br/>Écart-type : ${point.ecartType.toFixed(2)}`,
    },
    xAxis: {
      ...axeValeur({ nom: 'Impact', formatteur: (v) => `${v}` }),
      min: xMin,
      max: xMax,
      nameLocation: 'middle',
      nameGap: 40,
      splitLine: { show: false },
    },
    yAxis: {
      ...axeValeur({ nom: 'Robustesse' }),
      min: yMin,
      max: yMax,
      axisLabel: { show: false },
      splitLine: { show: false },
    },
    series: [
      {
        type: 'scatter',
        symbolSize: 18,
        itemStyle: { color: '#1F3864' },
        label: {
          show: true,
          position: 'right',
          fontSize: 14,
          color: '#000000',
          formatter: ({ data: point }) => String(point.numero),
        },
        // Numéro = rang dans la liste reçue (1, 2, 3...), repris dans le tableau.
        data: data.map((d, i) => ({
          nom: d.solution,
          numero: i + 1,
          ecartType: d.ecartType,
          value: [d.impact, versY(d.ecartType)],
        })),
        markArea: {
          silent: true,
          itemStyle: { opacity: 1 },
          data: [
            // Les quatre quadrants, bornés par 0 en X et la moitié de l'axe Y.
            [{ xAxis: xMin, yAxis: yMax / 2, itemStyle: { color: QUADRANTS[0].couleur } },
             { xAxis: 0, yAxis: yMax }],
            [{ xAxis: 0, yAxis: yMax / 2, itemStyle: { color: QUADRANTS[1].couleur } },
             { xAxis: xMax, yAxis: yMax }],
            [{ xAxis: xMin, yAxis: yMin, itemStyle: { color: QUADRANTS[2].couleur } },
             { xAxis: 0, yAxis: yMax / 2 }],
            [{ xAxis: 0, yAxis: yMin, itemStyle: { color: QUADRANTS[3].couleur } },
             { xAxis: xMax, yAxis: yMax / 2 }],
          ],
        },
      },
    ],
    graphic: [
      {
        type: 'text', left: 4, top: 46,
        style: { text: 'sans regret', fontSize: 14, fill: '#000000', fontWeight: 'bold' },
      },
      {
        type: 'text', left: 4, bottom: 70,
        style: { text: 'dépend des\nscénarios', fontSize: 14, fill: '#000000', fontWeight: 'bold' },
      },
      {
        type: 'text', left: 100, bottom: 8,
        style: { text: 'impact faible', fontSize: 14, fill: '#000000' },
      },
      {
        type: 'text', right: 40, bottom: 8,
        style: { text: 'impact fort', fontSize: 14, fill: '#000000' },
      },
    ],
  };

  // Graphique à gauche, tableau de correspondance numéro -> nom complet à droite.
  return (
    <div className="oad-pheno-layout" style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ flex: '1.5 1 0', minWidth: 0, display: 'flex' }}>
        <ResponsiveECharts option={option} style={{ flex: '1 1 auto', minHeight: 460 }} />
      </div>

      <div style={{ flex: '1 1 0', overflowX: 'auto' }}>
        <table className="oad-data-table">
          <thead>
            <tr>
              <th>N°</th>
              <th>Solution d'adaptation</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d, i) => (
              <tr key={d.solution}>
                <td><b>{i + 1}</b></td>
                <td>{d.solution}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
